import { useState, useEffect, useCallback, useRef } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { useSchool } from '../context/SchoolContext';
import { useLiveMode } from '../context/LiveModeContext';
import {
  Sparkles, Loader2, FileText, Clock, Download, Copy,
  CalendarIcon, AlertTriangle, Square, ArrowRight,
  Check, RefreshCw, Pencil, CheckCircle2, X, Info
} from 'lucide-react';
import { toast } from 'sonner';
import { format, subDays, isToday, isYesterday } from 'date-fns';
import { API_BASE } from '../apiConfig';

const API = API_BASE;

export const SummariesPage = () => {
  const { selectedSchool } = useSchool();
  const { session: liveSession, endSession } = useLiveMode();

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [selectedSessionIds, setSelectedSessionIds] = useState([]);
  const [recentSession, setRecentSession] = useState(null);
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [bulkSummaries, setBulkSummaries] = useState([]);
  const [summaryHistory, setSummaryHistory] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [regeneratingIdx, setRegeneratingIdx] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [endingLesson, setEndingLesson] = useState(false);
  const [editingIdx, setEditingIdx] = useState(null);
  const [showAttendanceWarning, setShowAttendanceWarning] = useState(false);
  const editRef = useRef(null);

  // Manual fallback state
  const [manualMode, setManualMode] = useState(false);
  const [classes, setClasses] = useState([]);
  const [manualClassId, setManualClassId] = useState('');
  const [manualPeriod, setManualPeriod] = useState('');
  const periodsCount = selectedSchool?.periods_per_day || 6;

  // Fetch sessions for selected date
  const fetchSessions = useCallback(async (date) => {
    setLoadingSessions(true);
    setSelectedSessionIds([]);
    setBulkSummaries([]);
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const r = await fetch(`${API}/teaching-sessions/by-date?date=${dateStr}`, { credentials: 'include' });
      if (r.ok) {
        const data = await r.json();
        setSessions(data);
        setManualMode(data.length === 0);
      }
    } catch (e) { console.error('Failed to fetch sessions:', e); }
    finally { setLoadingSessions(false); }
  }, []);

  // Fetch most recent completed session
  useEffect(() => {
    const fetchRecent = async () => {
      try {
        const r = await fetch(`${API}/teaching-sessions/recent`, { credentials: 'include' });
        if (r.ok) {
          const data = await r.json();
          if (data.session_id) setRecentSession(data);
        }
      } catch (e) { /* ignore */ }
    };
    fetchRecent();
  }, []);

  // Fetch classes for manual fallback
  useEffect(() => {
    if (!selectedSchool) return;
    const fetchClasses = async () => {
      try {
        const r = await fetch(`${API}/classes?school_id=${selectedSchool.school_id}`, { credentials: 'include' });
        if (r.ok) {
          const data = await r.json();
          setClasses(data.filter(c => !c.archived));
        }
      } catch (e) { /* ignore */ }
    };
    fetchClasses();
  }, [selectedSchool]);

  // Fetch sessions when date changes
  useEffect(() => { fetchSessions(selectedDate); }, [selectedDate, fetchSessions]);

  // Fetch summary history for the first selected session's class
  useEffect(() => {
    const firstSelected = sessions.find(s => selectedSessionIds.includes(s.session_id));
    const classId = firstSelected?.class_id || manualClassId;
    if (!classId) { setSummaryHistory([]); return; }
    const fetchHistory = async () => {
      setLoadingHistory(true);
      try {
        const r = await fetch(`${API}/ai-summary/${classId}/history`, { credentials: 'include' });
        if (r.ok) setSummaryHistory(await r.json());
      } catch (e) { /* ignore */ }
      finally { setLoadingHistory(false); }
    };
    fetchHistory();
  }, [selectedSessionIds, sessions, manualClassId]);

  const isSessionActive = (sess) => !sess.ended_at;

  const toggleSession = (sessionId) => {
    setSelectedSessionIds(prev =>
      prev.includes(sessionId) ? prev.filter(id => id !== sessionId) : [...prev, sessionId]
    );
  };

  const selectAllSessions = () => {
    const endedIds = sessions.filter(s => !isSessionActive(s)).map(s => s.session_id);
    setSelectedSessionIds(prev => prev.length === endedIds.length ? [] : endedIds);
  };

  const selectedSessions = sessions.filter(s => selectedSessionIds.includes(s.session_id));

  // Generate summary for a single session (returns the result)
  const generateOne = async (sess) => {
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const r = await fetch(`${API}/ai-summary`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        class_id: sess.class_id,
        date: dateStr,
        period: sess.period || null,
        additional_notes: additionalNotes || null,
        session_id: sess.session_id,
      }),
    });
    if (r.ok) return await r.json();
    const err = await r.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to generate');
  };

  // Bulk generate for all selected sessions
  const handleBulkGenerate = async () => {
    if (selectedSessions.length === 0) return;
    setGenerating(true);
    setBulkSummaries([]);
    setEditingIdx(null);
    setShowAttendanceWarning(false);

    const results = [];
    let anyMissingAttendance = false;
    for (const sess of selectedSessions.sort((a, b) => a.period - b.period)) {
      try {
        const data = await generateOne(sess);
        if (data.attendance_taken === false) anyMissingAttendance = true;
        results.push({ session: sess, summary: data.summary, class_name: data.class_name || sess.class_name });
        setBulkSummaries([...results]);
      } catch (e) {
        results.push({ session: sess, summary: `Error: ${e.message}`, class_name: sess.class_name, error: true });
        setBulkSummaries([...results]);
      }
    }
    setBulkSummaries(results);
    setGenerating(false);
    if (anyMissingAttendance) setShowAttendanceWarning(true);
    if (results.some(r => !r.error)) toast.success(`${results.filter(r => !r.error).length} summary(s) generated`);
  };

  // Regenerate a single summary within bulk results
  const handleRegenerate = async (idx) => {
    const item = bulkSummaries[idx];
    if (!item) return;
    setRegeneratingIdx(idx);
    try {
      const data = await generateOne(item.session);
      setBulkSummaries(prev => prev.map((s, i) => i === idx ? { ...s, summary: data.summary, error: false } : s));
      toast.success('Summary regenerated');
    } catch (e) {
      toast.error(`Failed to regenerate: ${e.message}`);
    }
    setRegeneratingIdx(null);
  };

  // End active lesson then generate
  const handleEndAndGenerate = async (sess) => {
    setEndingLesson(true);
    try {
      await fetch(`${API}/teaching-sessions/end/${sess.session_id}`, { method: 'POST', credentials: 'include' });
      if (liveSession?.session_id === sess.session_id) await endSession();
      toast.success('Lesson ended');
      fetchSessions(selectedDate);
    } catch (e) {
      toast.error('Failed to end lesson');
      setEndingLesson(false);
      return;
    }
    setEndingLesson(false);
  };

  // Manual mode generate
  const handleManualGenerate = async () => {
    if (!manualClassId) { toast.error('Select a class'); return; }
    setGenerating(true);
    setBulkSummaries([]);
    setShowAttendanceWarning(false);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const r = await fetch(`${API}/ai-summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          class_id: manualClassId,
          date: dateStr,
          period: manualPeriod ? parseInt(manualPeriod) : null,
          additional_notes: additionalNotes || null,
          session_id: null,
        }),
      });
      if (r.ok) {
        const data = await r.json();
        const cls = classes.find(c => c.class_id === manualClassId);
        setBulkSummaries([{ session: { class_id: manualClassId, period: manualPeriod || null }, summary: data.summary, class_name: data.class_name || cls?.name || 'Class' }]);
        if (data.attendance_taken === false) setShowAttendanceWarning(true);
        toast.success('Summary generated');
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to generate');
      }
    } catch (e) {
      toast.error('Failed to generate summary');
    } finally {
      setGenerating(false);
    }
  };

  // Inline editing
  const startEditing = (idx) => {
    setEditingIdx(idx);
    setTimeout(() => editRef.current?.focus(), 50);
  };
  const saveEdit = (idx, newText) => {
    setBulkSummaries(prev => prev.map((s, i) => i === idx ? { ...s, summary: newText } : s));
    setEditingIdx(null);
    toast.success('Summary updated');
  };
  const cancelEdit = () => setEditingIdx(null);

  // Copy & export
  const getCombinedText = () => {
    return bulkSummaries.map(item => {
      const header = `Period ${item.session.period || '?'} — ${item.class_name}`;
      return `${header}\n${'—'.repeat(header.length)}\n${item.summary}`;
    }).join('\n\n');
  };

  const handleCopyAll = () => {
    navigator.clipboard.writeText(getCombinedText());
    toast.success('All summaries copied');
  };

  const handleCopySingle = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleExportPDF = async () => {
    if (bulkSummaries.length === 0) return;
    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      const dateStr = format(selectedDate, 'dd MMM yyyy');
      doc.setFontSize(18);
      doc.text(`Lesson Summaries — ${dateStr}`, 14, 20);
      let y = 32;
      for (const item of bulkSummaries) {
        if (y > 260) { doc.addPage(); y = 20; }
        doc.setFontSize(13);
        doc.setFont(undefined, 'bold');
        doc.text(`Period ${item.session.period || '?'} — ${item.class_name}`, 14, y);
        y += 7;
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const lines = doc.splitTextToSize(item.summary, 180);
        for (const line of lines) {
          if (y > 280) { doc.addPage(); y = 20; }
          doc.text(line, 14, y);
          y += 5;
        }
        y += 8;
      }
      doc.save(`summaries-${format(selectedDate, 'yyyy-MM-dd')}.pdf`);
      toast.success('PDF downloaded');
    } catch (e) {
      toast.error('PDF export failed');
    }
  };

  const handleSelectRecent = () => {
    if (!recentSession) return;
    const sessDate = new Date(recentSession.date + 'T12:00:00');
    setSelectedDate(sessDate);
    setSelectedSessionIds([recentSession.session_id]);
    setManualMode(false);
  };

  const dateLabel = isToday(selectedDate) ? 'Today' : isYesterday(selectedDate) ? 'Yesterday' : format(selectedDate, 'dd MMM yyyy');
  const endedSessions = sessions.filter(s => !isSessionActive(s));
  const activeSessions = sessions.filter(s => isSessionActive(s));
  const hasActiveSelected = selectedSessions.some(s => isSessionActive(s));

  return (
    <Layout breadcrumb={[{ label: 'End of Lesson Summaries' }]}>
      <div className="space-y-5 max-w-3xl" data-testid="summaries-page">

        {/* Recent lesson shortcut */}
        {recentSession && bulkSummaries.length === 0 && (
          <button
            onClick={handleSelectRecent}
            className="w-full bg-teal-50 hover:bg-teal-100 border border-teal-100 rounded-xl p-4 flex items-center justify-between transition-colors text-left animate-fade-in"
            data-testid="recent-lesson-shortcut"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-teal-600 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-indigo-800">Resume last lesson summary</p>
                <p className="text-xs text-teal-500 mt-0.5">
                  {recentSession.class_name} — Period {recentSession.period} — {recentSession.date}
                </p>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-indigo-400 flex-shrink-0" />
          </button>
        )}

        {/* Date selector & session picker */}
        <Card className="bg-white border border-slate-200 shadow-soft" data-testid="summary-selector">
          <CardContent className="p-5">
            <h1 className="text-lg font-heading font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-teal-500" /> AI Summaries
            </h1>

            {/* Date chips + calendar */}
            <div className="flex items-center gap-2 flex-wrap mb-5">
              <button
                onClick={() => setSelectedDate(new Date())}
                className={`px-4 h-9 rounded-xl text-sm font-medium transition-all ${isToday(selectedDate) ? 'bg-teal-600 text-white shadow-sm' : 'bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100'}`}
                data-testid="date-chip-today"
              >Today</button>
              <button
                onClick={() => setSelectedDate(subDays(new Date(), 1))}
                className={`px-4 h-9 rounded-xl text-sm font-medium transition-all ${isYesterday(selectedDate) ? 'bg-teal-600 text-white shadow-sm' : 'bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100'}`}
                data-testid="date-chip-yesterday"
              >Yesterday</button>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={`rounded-xl border-slate-200 text-sm h-9 font-medium ${!isToday(selectedDate) && !isYesterday(selectedDate) ? 'bg-teal-600 text-white border-teal-600 hover:bg-teal-700' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`}
                    data-testid="summary-date-picker"
                  >
                    <CalendarIcon className="w-4 h-4 mr-1.5" />
                    {!isToday(selectedDate) && !isYesterday(selectedDate) ? format(selectedDate, 'dd MMM') : 'Pick date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => { if (date) { setSelectedDate(date); setCalendarOpen(false); } }}
                    disabled={(date) => date > new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Session list with multi-select */}
            {loadingSessions ? (
              <div className="flex items-center gap-2 text-sm text-slate-400 py-6">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading lessons...
              </div>
            ) : sessions.length > 0 && !manualMode ? (
              <div className="space-y-2 mb-5" data-testid="session-list">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-slate-500">Lessons on {dateLabel}</label>
                  {endedSessions.length > 1 && (
                    <button
                      onClick={selectAllSessions}
                      className="text-xs text-teal-600 hover:text-indigo-800 font-medium"
                      data-testid="select-all-sessions-btn"
                    >
                      {selectedSessionIds.length === endedSessions.length ? 'Deselect all' : 'Select all'}
                    </button>
                  )}
                </div>

                {/* Active sessions warning */}
                {activeSessions.map((sess) => (
                  <div key={sess.session_id} className="w-full text-left p-3.5 rounded-xl border border-amber-200 bg-amber-50 flex items-center justify-between" data-testid={`session-item-active-${sess.session_id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse flex-shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-amber-800">
                          {sess.class_name}
                          {sess.class_code && <span className="font-normal text-amber-500 ml-1">({sess.class_code})</span>}
                        </p>
                        <p className="text-xs text-amber-600">Period {sess.period} <span className="font-medium ml-1">Active — end to generate</span></p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleEndAndGenerate(sess)}
                      disabled={endingLesson}
                      className="bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs h-8"
                      data-testid={`end-lesson-btn-${sess.session_id}`}
                    >
                      {endingLesson ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Square className="w-3 h-3 mr-1 fill-current" /> End</>}
                    </Button>
                  </div>
                ))}

                {/* Ended sessions (selectable) */}
                {endedSessions.map((sess) => {
                  const isSelected = selectedSessionIds.includes(sess.session_id);
                  return (
                    <button
                      key={sess.session_id}
                      onClick={() => toggleSession(sess.session_id)}
                      className={`w-full text-left p-3.5 rounded-xl border transition-all flex items-center justify-between ${
                        isSelected ? 'bg-teal-50 border-teal-200 ring-1 ring-teal-200' : 'bg-slate-50 border-slate-100 hover:bg-slate-100'
                      }`}
                      data-testid={`session-item-${sess.session_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                          isSelected ? 'bg-teal-600 border-teal-600' : 'border-slate-300 bg-white'
                        }`}>
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <div>
                          <p className={`text-sm font-semibold ${isSelected ? 'text-indigo-800' : 'text-slate-800'}`}>
                            {sess.class_name}
                            {sess.class_code && <span className="font-normal text-slate-400 ml-1">({sess.class_code})</span>}
                          </p>
                          <p className="text-xs text-slate-500">Period {sess.period}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}

                <button
                  onClick={() => setManualMode(true)}
                  className="text-xs text-teal-600 hover:text-indigo-800 font-medium mt-1"
                  data-testid="switch-manual-btn"
                >Or select class manually...</button>
              </div>
            ) : (
              /* Manual fallback */
              <div className="mb-5" data-testid="manual-fallback">
                {sessions.length === 0 && (
                  <div className="rounded-xl bg-slate-50 border border-slate-100 p-3 mb-4 flex items-start gap-2.5">
                    <FileText className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-slate-500">No lessons found for {dateLabel}. Select manually or start a lesson first.</p>
                  </div>
                )}
                {sessions.length > 0 && (
                  <button onClick={() => setManualMode(false)} className="text-xs text-teal-600 hover:text-indigo-800 font-medium mb-3" data-testid="switch-session-btn">
                    Back to lesson list
                  </button>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1.5 block">Class</label>
                    <Select value={manualClassId} onValueChange={setManualClassId}>
                      <SelectTrigger className="w-full rounded-xl border-slate-200 bg-slate-50 text-sm h-11" data-testid="manual-class-select">
                        <SelectValue placeholder="Select a class..." />
                      </SelectTrigger>
                      <SelectContent>
                        {classes.map(c => (
                          <SelectItem key={c.class_id} value={c.class_id}>{c.name}{c.class_code ? ` (${c.class_code})` : ''}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1.5 block">Period (optional)</label>
                    <div className="flex gap-1.5 flex-wrap">
                      <button onClick={() => setManualPeriod('')} className={`px-3 h-10 rounded-xl text-xs font-semibold transition-all ${manualPeriod === '' ? 'bg-teal-600 text-white shadow-sm' : 'bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100'}`} data-testid="manual-period-all">All</button>
                      {Array.from({ length: periodsCount }, (_, i) => i + 1).map(p => (
                        <button key={p} onClick={() => setManualPeriod(String(p))} className={`w-10 h-10 rounded-xl text-sm font-semibold transition-all ${String(p) === manualPeriod ? 'bg-teal-600 text-white shadow-sm' : 'bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100'}`} data-testid={`manual-period-${p}`}>{p}</button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Additional notes */}
            <div className="mb-4">
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">Additional notes (optional)</label>
              <Textarea
                placeholder="Any extra context for the summary..."
                value={additionalNotes}
                onChange={(e) => setAdditionalNotes(e.target.value)}
                className="border-slate-200 rounded-xl text-sm min-h-[60px] resize-none"
                data-testid="summary-notes"
              />
            </div>

            {/* Generate buttons */}
            {!manualMode && selectedSessionIds.length > 0 && !hasActiveSelected ? (
              <Button
                onClick={handleBulkGenerate}
                disabled={generating}
                className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl w-full sm:w-auto"
                data-testid="generate-summary-btn"
              >
                {generating
                  ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generating {bulkSummaries.length}/{selectedSessionIds.length}...</>
                  : <><Sparkles className="w-4 h-4 mr-2" /> Generate {selectedSessionIds.length > 1 ? `${selectedSessionIds.length} Summaries` : 'Summary'}</>
                }
              </Button>
            ) : manualMode ? (
              <Button
                onClick={handleManualGenerate}
                disabled={!manualClassId || generating}
                className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl w-full sm:w-auto"
                data-testid="generate-summary-btn"
              >
                {generating ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generating...</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate Summary</>}
              </Button>
            ) : null}
          </CardContent>
        </Card>

        {/* Bulk summary results */}
        {bulkSummaries.length > 0 && (
          <Card className="bg-white border border-slate-200 shadow-soft animate-fade-in" data-testid="summary-result">
            <CardContent className="p-5">
              {/* Header with actions */}
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-heading font-semibold text-slate-800 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-teal-500" />
                  {bulkSummaries.length > 1 ? `${bulkSummaries.length} Summaries` : 'Summary'} — {dateLabel}
                </h2>
                <div className="flex items-center gap-1.5">
                  <button onClick={handleCopyAll} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors" title="Copy all" data-testid="copy-all-btn">
                    <Copy className="w-4 h-4" />
                  </button>
                  <button onClick={handleExportPDF} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors" title="Download PDF" data-testid="download-pdf-btn">
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Attendance warning */}
              {showAttendanceWarning && (
                <div className="mt-4 p-3.5 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-3" data-testid="attendance-warning">
                  <Info className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Attendance Not Recorded</p>
                    <p className="text-xs text-amber-600 mt-0.5">
                      Attendance was not completed for one or more sessions. Students who were absent may appear in this feedback.
                      Please review the summary and edit where necessary using the pencil icon.
                    </p>
                    <button
                      onClick={() => setShowAttendanceWarning(false)}
                      className="text-xs text-amber-700 font-medium mt-2 hover:underline"
                      data-testid="dismiss-attendance-warning"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              )}

              {/* Individual summary sections */}
              <div className="space-y-5">
                {bulkSummaries.map((item, idx) => (
                  <SummaryBlock
                    key={idx}
                    item={item}
                    idx={idx}
                    isEditing={editingIdx === idx}
                    isRegenerating={regeneratingIdx === idx}
                    editRef={editingIdx === idx ? editRef : null}
                    onStartEdit={() => startEditing(idx)}
                    onSaveEdit={(text) => saveEdit(idx, text)}
                    onCancelEdit={cancelEdit}
                    onRegenerate={() => handleRegenerate(idx)}
                    onCopy={() => handleCopySingle(item.summary)}
                  />
                ))}
              </div>

              {generating && bulkSummaries.length < selectedSessionIds.length && (
                <div className="flex items-center gap-2 text-sm text-teal-500 mt-4 pt-4 border-t border-slate-100">
                  <Loader2 className="w-4 h-4 animate-spin" /> Generating next summary...
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Summary history */}
        {(selectedSessionIds.length > 0 || manualClassId) && (
          <Card className="bg-white border border-slate-200 shadow-soft" data-testid="summary-history">
            <CardContent className="p-5">
              <h2 className="text-sm font-heading font-semibold text-slate-800 mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-400" /> Previous Summaries
              </h2>
              {loadingHistory ? (
                <div className="flex items-center gap-2 text-sm text-slate-400 py-4"><Loader2 className="w-4 h-4 animate-spin" /> Loading...</div>
              ) : summaryHistory.length === 0 ? (
                <div className="text-center py-6"><FileText className="w-8 h-8 text-slate-200 mx-auto mb-2" /><p className="text-sm text-slate-400">No summaries yet</p></div>
              ) : (
                <div className="space-y-3">
                  {summaryHistory.map((item, i) => (
                    <div key={i} className="p-3 rounded-xl bg-slate-50 border border-slate-100 cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => {
                      setBulkSummaries([{ session: { class_id: item.class_id, period: item.period }, summary: item.summary, class_name: item.class_name || 'Class' }]);
                    }} data-testid={`history-item-${i}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-slate-600">{item.date}{item.period && <span className="text-slate-400"> — Period {item.period}</span>}</span>
                        <span className="text-[10px] text-slate-400">{item.created_at ? new Date(item.created_at).toLocaleDateString() : ''}</span>
                      </div>
                      <p className="text-xs text-slate-500 line-clamp-2">{item.summary}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

// Inline-editable summary block
const SummaryBlock = ({ item, idx, isEditing, isRegenerating, editRef, onStartEdit, onSaveEdit, onCancelEdit, onRegenerate, onCopy }) => {
  const [editText, setEditText] = useState(item.summary);

  useEffect(() => { setEditText(item.summary); }, [item.summary]);

  return (
    <div className={`${idx > 0 ? 'pt-5 border-t border-slate-100' : ''}`} data-testid={`summary-block-${idx}`}>
      {/* Period header */}
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold text-teal-600 uppercase tracking-wide flex items-center gap-1.5">
          Period {item.session.period || '?'} — {item.class_name}
        </h3>
        <div className="flex items-center gap-1">
          {!isEditing && (
            <>
              <button
                onClick={onStartEdit}
                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-teal-600 transition-colors"
                title="Edit"
                data-testid={`edit-summary-btn-${idx}`}
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={onRegenerate}
                disabled={isRegenerating}
                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-teal-600 transition-colors disabled:opacity-50"
                title="Regenerate"
                data-testid={`regenerate-btn-${idx}`}
              >
                {isRegenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              </button>
              <button
                onClick={onCopy}
                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                title="Copy"
                data-testid={`copy-summary-btn-${idx}`}
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Summary text — inline editable */}
      {isEditing ? (
        <div className="space-y-2">
          <textarea
            ref={editRef}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            className="w-full text-sm text-slate-700 bg-teal-50 border border-teal-200 rounded-xl p-3 min-h-[150px] resize-y focus:outline-none focus:ring-2 focus:ring-teal-300"
            data-testid={`edit-textarea-${idx}`}
          />
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => onSaveEdit(editText)} className="bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-xs h-8" data-testid={`save-edit-btn-${idx}`}>
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Save
            </Button>
            <Button size="sm" variant="ghost" onClick={onCancelEdit} className="text-slate-500 rounded-lg text-xs h-8" data-testid={`cancel-edit-btn-${idx}`}>
              <X className="w-3.5 h-3.5 mr-1" /> Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div
          className={`prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap ${item.error ? 'text-red-500' : ''}`}
          data-testid={`summary-text-${idx}`}
        >
          {item.summary}
        </div>
      )}
    </div>
  );
};

export default SummariesPage;
